import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { Clock, Scale, ArrowRight, Plus, Sparkles } from 'lucide-react';
/**
 * SignalWordCard — Compact card displaying a Dutch signal word and its function.
 *
 * Pairs with the `signal_word_function` MC mode (post-fout support) and
 * serves as standalone stimulus for LEZEN-STRUCTUUR.
 *
 * Audit reference: TAA_VRAAGTYPEN_VISUALS_AUDIT_2026.md V2
 */
export type SignalFunction = 'oorzaak-gevolg' | 'tijd' | 'tegenstelling' | 'toevoeging' | 'overig';
interface SignalWordCardProps {
  word: string;
  function: SignalFunction;
  example?: string;
  label?: string;
  mode?: 'stimulus' | 'support';
}
interface FunctionStyle {
  bg: string;
  border: string;
  iconBg: string;
  iconColor: string;
  text: string;
  Icon: ComponentType<{
    className?: string;
  }>;
  defaultLabel: string;
}
const FUNCTION_STYLES: Record<SignalFunction, FunctionStyle> = {
  'oorzaak-gevolg': {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-700',
    text: 'text-orange-900',
    Icon: ArrowRight,
    defaultLabel: 'Oorzaak-gevolg'
  },
  tijd: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-700',
    text: 'text-blue-900',
    Icon: Clock,
    defaultLabel: 'Tijd'
  },
  tegenstelling: {
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-700',
    text: 'text-violet-900',
    Icon: Scale,
    defaultLabel: 'Tegenstelling'
  },
  toevoeging: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-700',
    text: 'text-emerald-900',
    Icon: Plus,
    defaultLabel: 'Toevoeging'
  },
  overig: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-700',
    text: 'text-slate-900',
    Icon: Sparkles,
    defaultLabel: 'Signaalwoord'
  }
};
export function SignalWordCard({
  word,
  function: fn,
  example,
  label,
  mode = 'support'
}: SignalWordCardProps) {
  const style = FUNCTION_STYLES[fn] ?? FUNCTION_STYLES.overig;
  const isStimulus = mode === 'stimulus';
  const {
    Icon
  } = style;
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className={`rounded-xl border ${style.bg} ${style.border} ${isStimulus ? 'p-3.5' : 'p-4'}`} role="group" aria-label={label || `Signaalwoord ${word}: ${style.defaultLabel}`} data-id="element-9896">
      <div className="flex items-start gap-3" data-id="element-9897">
        <div className={`rounded-lg flex items-center justify-center flex-shrink-0 ${style.iconBg} ${isStimulus ? 'w-9 h-9' : 'w-11 h-11'}`} data-id="element-9898">
          <Icon className={`${style.iconColor} ${isStimulus ? 'w-5 h-5' : 'w-6 h-6'}`} data-id="element-9899" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-9900">
          <div className="flex items-baseline gap-2 flex-wrap" data-id="element-9901">
            <span className={`font-bold ${style.text} ${isStimulus ? 'text-base' : 'text-lg'}`} data-id="element-9902">
              {word}
            </span>
            <span className={`uppercase tracking-wide font-semibold ${style.iconColor} ${isStimulus ? 'text-[10px]' : 'text-xs'}`} data-id="element-9903">
              {label || style.defaultLabel}
            </span>
          </div>
          {example && <p className={`mt-1.5 text-slate-700 leading-relaxed ${isStimulus ? 'text-xs' : 'text-sm'}`} data-id="element-9904">
              {example}
            </p>}
        </div>
      </div>
    </motion.div>;
}